import { ApiError } from "@/lib/errors/api-error";
import { handleApiError } from "@/lib/errors/error-handler";
import {
  LoginForm,
  PersonalDetail,
  RegisterForm,
  SendOTPPhone,
  VerificationForm,
  VerifyEmail,
  VerifyOTPPhone,
  Request2FAOtpPhone,
  Verify2FAOtpPhone,
  Complete2FA,
  FcmStatus
} from "@/types/auth";

export type SessionStatus = {
  authenticated: boolean;
  success: boolean;
  individualCustomerId: number;
  individualCustomerName?: string;
  expiresAt?: number | null;
};

async function getJson<T>(path: string) {
  const res = await fetch(path, { method: "GET", credentials: "include" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    return { success: false, authenticated: false } as T;
  }
  return (data?.data ?? data) as T;
}

type ResetRequestPayload = {
  email: string;
};

type ResetPasswordPayload = {
  email: string;
  token: string;
  password: string;
  password_confirmation?: string;
};
type SetPasswordPayload = {
  email: string;
  token: string;
  password: string;
};

async function postJson(path: string, body: unknown) {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    /* The status is already known here, so build the error from it. Handing a bare Error
       to handleApiError instead sent it down that helper's last resort -- the branch for
       values thrown by something that never said what went wrong -- which logged every
       ordinary rejection as "Unknown error" with a stack, and relabelled it 500. A 429 on
       the register endpoint, or a 422 for an email already taken, is neither unknown nor
       a server fault, and the caller puts the message on the form either way. */
    throw new ApiError(
      res.status,
      typeof data?.message === "string" ? data.message : "Failed to complete request",
      typeof data?.error?.code === "string" ? data.error.code : undefined,
      data,
    );
  }
  return data;
}

export const authService = {
  session(): Promise<SessionStatus> {
    return getJson<SessionStatus>("/api/auth/session");
  },

  async requestReset(payload: ResetRequestPayload) {
    const data = await postJson("/api/auth/password/email", payload);
    if (data?.success === false) {
      throw handleApiError(new Error(data?.message || "Failed to send reset link"));
    }
    return data;
  },

  async resetPassword(payload: ResetPasswordPayload) {
    return postJson("/api/auth/password/reset", payload);
  },

  async setPassword(payload: SetPasswordPayload) {
    return postJson("/api/auth/set-password", payload);
  },

  async resendVerification(payload: ResetRequestPayload) {
    return postJson("/api/auth/resend-email", payload);
  },

  async resendOtp(payload: ResetRequestPayload) {
    return postJson("/api/auth/resend-otp", payload);
  },

  async verifyEmail(payload: VerifyEmail) {
    return postJson("/api/auth/verify-email", payload);
  },

  async sendOtpPhone(payload: SendOTPPhone) {
    return postJson("/api/auth/send-otp", payload);
  },

  async verifyOtpPhone(payload: VerifyOTPPhone) {
    return postJson("/api/auth/verify-otp", payload);
  },

  async login(payload: LoginForm) {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));

    return data;
  },

  async request2FACode(payload: Request2FAOtpPhone) {
    return postJson("/api/auth/login/two-factor/request-otp", payload);
  },

  async verify2FAOtpPhone(payload: Verify2FAOtpPhone) {
    return postJson("/api/auth/login/two-factor/verify-otp", payload);
  },

  async complete2FA(payload: Complete2FA) {
    return postJson("/api/auth/login/two-factor/complete", payload);
  },

  async getFCMstatus(challenge_id: string) {
    return getJson<FcmStatus>(
      `/api/auth/login/two-factor/fcm-status?challenge_id=${challenge_id}`
    );
  },
  
  async register(payload: RegisterForm) {
    return postJson("/api/auth/register", payload);
  },

  async verificationGreenId(payload: VerificationForm) {
    return postJson("/api/auth/verify-greenid", payload);
  },

  async updatePersonalDetail(payload: PersonalDetail) {
    const res = await fetch("/api/auth/update-personal-details", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw handleApiError(
        new Error(typeof data?.message === "string" ? data.message : "Failed to complete request"),
      );
    }
    return data;
  },

  async checkEmail(payload: ResetRequestPayload) {
    const res = await fetch("/api/auth/check-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));

    return data;
  },

  async checkPhone(payload: { mobile: string }) {
    return postJson("/api/auth/check-phone", payload);
  },

  /** Whether a session_kick_out event's device_id is this browser's (see the route). */
  async isSessionKickOutForThisBrowser(deviceId: string): Promise<boolean> {
    const data = await postJson("/api/auth/session-kick-out", { device_id: deviceId });
    return data?.data?.signOut === true;
  },
};
